import React, { useState, useEffect } from 'react';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FaMagic, FaComments, FaArrowRight, FaCalendarCheck, FaHistory, FaPlus, FaExclamationTriangle, FaTag } from 'react-icons/fa';

const serviceNameMap = {
    'dietitian': 'AI Dietitian',
    'astrologer': 'AI Astrologer',
    'ayurvedacharya': 'AI Ayurvedacharya',
    'reiki-healer': 'AI Reiki Healer',
    'numerologer': 'AI Numerologer',
    'tarot-card-reader': 'AI Tarot Card Reader',
    'home-tutor': 'AI Home Tutor'
};

const MyAiConsultants = () => {
    const [enrollments, setEnrollments] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        fetchEnrollments();
    }, []);

    const fetchEnrollments = async () => {
        try {
            setLoading(true);
            const res = await HttpInterceptor.get('/api/ai/enrollments');
            if (res.data?.success) {
                setEnrollments(res.data.data);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to load AI enrollments');
        } finally {
            setLoading(false);
        }
    };

    const getConsultantImage = (service) => {
        switch (service?.toLowerCase().replace(/\s+/g, '-')) {
            case 'dietitian': return "/assets/ai-agents/ananya_dietitian.png";
            case 'astrologer': return "/assets/ai-agents/prakash_astrologer.png";
            case 'ayurvedacharya': return "/assets/ai-agents/narayan_ayurveda.png";
            case 'reiki-healer': return "/assets/ai-agents/kavya_reiki.png";
            case 'numerologer': return "/assets/ai-agents/vedika_numerology.png";
            case 'tarot-card-reader': return "/assets/ai-agents/roshni_tarot.png";
            case 'home-tutor': return "/assets/ai-agents/vidyasagar_tutor.png";
            default: return "/assets/ai-agents/priya_representative.png";
        }
    };

    const startChat = async (serviceType) => {
        try {
            const res = await HttpInterceptor.post('/api/ai/chat-sessions', {
                serviceType,
                title: "New Session"
            });
            if (res.data?.success) {
                navigate(`/student/ai-chat/${res.data.data._id}`);
            } else {
                toast.error('Failed to start chat session');
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to start chat session');
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                    <FaMagic className="text-purple-600" />
                    My AI Consultants
                </h1>
                <p className="mt-2 text-gray-600">
                    Access your enrolled AI expert consultants and manage your daily queries.
                </p>
            </div>

            {enrollments.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center max-w-2xl mx-auto">
                    <div className="w-20 h-20 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FaMagic className="w-10 h-10 text-purple-400" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">No AI Consultants Yet</h3>
                    <p className="text-gray-600 mb-6">
                        You haven't enrolled with any AI consultants yet. Browse our consultants section to find expert AI agents tailored to your needs.
                    </p>
                    <button
                        onClick={() => navigate('/student/services')}
                        className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold shadow-md hover:shadow-lg transition-all"
                    >
                        Browse Consultants
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {enrollments.map((enrollment) => (
                        <div key={enrollment._id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group shrink-0">
                            <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-6 flex items-start justify-between text-white relative h-32">
                                <div className="relative z-10 flex items-center gap-4">
                                    <div className="w-16 h-16 rounded-full border-2 border-white/50 overflow-hidden shadow-lg flex-shrink-0 bg-white/20">
                                        <img
                                            src={getConsultantImage(enrollment.serviceType)}
                                            alt={enrollment.serviceType}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.src = "/assets/ai-agents/priya_representative.png";
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold mb-1">
                                            {serviceNameMap[enrollment.serviceType] || enrollment.serviceType}
                                        </h3>
                                        <div className="flex items-center gap-1.5 text-xs text-purple-100 font-medium">
                                            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                                            Online
                                        </div>
                                    </div>
                                </div>
                                <div className="absolute top-4 right-4 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm shadow-inner transition-transform group-hover:scale-110">
                                    <FaMagic className="w-5 h-5 text-white" />
                                </div>
                            </div>

                            <div className="p-5">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex flex-col">
                                        <span className="text-sm text-gray-500 font-medium">Daily Queries Left</span>
                                        <span className="text-[9px] text-purple-600 font-bold uppercase tracking-tight">Free* (Shared Daily Limit)</span>
                                    </div>
                                    <div className={`px-2.5 py-1 text-xs font-bold rounded-full ${enrollment.queriesRemaining > 0
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-red-100 text-red-700'
                                        }`}>
                                        {enrollment.queriesRemaining} Queries
                                    </div>
                                </div>
                                <div className="mb-4 p-2 bg-yellow-50 border border-yellow-100 rounded-lg flex items-start gap-2">
                                    <FaExclamationTriangle className="text-amber-500 text-[10px] mt-0.5 flex-shrink-0" />
                                    <p className="text-[10px] leading-tight text-amber-800 font-medium">
                                        CAUTION: AI-generated advice. Not a substitute for professional medical diagnosis.
                                    </p>
                                </div>
                                <ul className="mb-4 space-y-1">
                                    <li className="flex items-start gap-2 text-sm text-gray-600">
                                        <span className="text-green-500 mt-0.5">✓</span>
                                        Free* daily queries (Shared limit, subject to change)
                                    </li>
                                </ul>

                                <div className="flex items-center justify-between mb-6 text-sm text-gray-500">
                                    <span className="flex items-center gap-1.5">
                                        <FaCalendarCheck className="text-purple-400" />
                                        Enrolled {new Date(enrollment.createdAt).toLocaleDateString()}
                                    </span>
                                    <span>
                                        Status: <span className="text-green-600 font-medium capitalize">{enrollment.status}</span>
                                    </span>
                                </div>

                                {enrollment.lastSessionId ? (
                                    <div className="space-y-3">
                                        <button
                                            onClick={() => navigate(`/student/ai-chat/${enrollment.lastSessionId}`)}
                                            className="w-full py-2.5 bg-purple-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-purple-700 transition-all shadow-md shadow-purple-100"
                                        >
                                            <FaComments />
                                            Continue Chat
                                        </button>
                                        <button
                                            onClick={() => startChat(enrollment.serviceType)}
                                            disabled={enrollment.status !== 'active' || enrollment.queriesRemaining <= 0}
                                            className="w-full py-2 text-xs font-bold text-gray-500 hover:text-purple-600 flex items-center justify-center gap-1.5 transition-colors"
                                        >
                                            <FaPlus className="text-[10px]" />
                                            Start New Session
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => startChat(enrollment.serviceType)}
                                        disabled={enrollment.status !== 'active' || enrollment.queriesRemaining <= 0}
                                        className="w-full py-2.5 bg-gray-50 border border-purple-200 text-purple-700 font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-purple-600 hover:text-white hover:border-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <FaComments />
                                        Start Consultation
                                        <FaArrowRight className="w-3 h-3 ml-1 opacity-10" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Explore More Card */}
                    <div
                        onClick={() => navigate('/student/services')}
                        className="bg-gray-50 rounded-2xl border-2 border-dashed border-purple-200 p-6 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-purple-50 hover:border-purple-300 transition-all group min-h-[300px]"
                    >
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 shadow-sm group-hover:scale-110 transition-transform">
                            <FaMagic className="w-8 h-8 text-purple-400" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-800 mb-2">Explore More Expertise</h3>
                        <p className="text-gray-500 text-sm mb-6 max-w-[200px]">
                            Need advice from other domains? Browse our full library of AI consultants.
                        </p>
                        <span className="text-purple-600 font-bold flex items-center gap-2">
                            Browse All Consultants
                            <FaArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};


export default MyAiConsultants;
